import axios from 'axios'
import type { AxiosError } from 'axios'
import type { ApiError } from '@/types'

let getToken: (() => Promise<string>) | null = null
let onAuthError: (() => void) | null = null
let onApiError: ((title: string, message: string) => void) | null = null

export function setTokenGetter(fn: () => Promise<string>) {
  getToken = fn
}

export function setAuthErrorHandler(fn: () => void) {
  onAuthError = fn
}

export function setApiErrorHandler(fn: (title: string, message: string) => void) {
  onApiError = fn
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

api.interceptors.request.use(async (config) => {
  if (getToken) {
    try {
      const token = await getToken()
      config.headers.Authorization = `Bearer ${token}`
    } catch {
      // Token retrieval failed, request will be sent without auth
    }
  }
  return config
})

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    if (error.response?.status === 401 && onAuthError) {
      onAuthError()
    } else if (onApiError) {
      if (!error.response) {
        onApiError('Connection Error', 'Unable to reach the server. Please check your internet connection and try again.')
      } else if (error.response.status >= 500) {
        onApiError('Server Error', 'Something went wrong on the server. Please try again later.')
      }
    }
    return Promise.reject(error)
  }
)

export default api
